import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import { config } from '../../config/config';
import type { SnackbarVariant } from '../../components/Snackbar';
import FloatingNewNoteButton from './FloatingNewNoteButton';
import ResearchNoteModal from './ResearchNoteModal';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import { applyQuillFontSizeStep, ensureQuillFontSizesRegistered } from '../../utils/quillFontSize';

ensureQuillFontSizesRegistered();

interface ResearchInsightsScreenProps {
  isDarkMode: boolean;
  selectedStock?: string;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

type InsightTab = 'Business Operations' | 'Strengths' | 'Weaknesses' | 'Opportunities' | 'Threats' | 'Moats' | 'Stock' | 'Other';

const INSIGHTS_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx';
const INSIGHTS_ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;
const INSIGHTS_ATTACHMENT_ALLOWED_EXTENSIONS = new Set(['.pdf', '.doc', '.docx']);


const ResearchInsightsScreen: React.FC<ResearchInsightsScreenProps> = ({
  isDarkMode,
  selectedStock,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
}) => {
  const [selectedTab, setSelectedTab] = useState<InsightTab>('Business Operations');
  const selectedTabRef = useRef<InsightTab>('Business Operations');
  const [businessOperationsContent, setBusinessOperationsContent] = useState('');
  const [strengthsContent, setStrengthsContent] = useState('');
  const [weaknessesContent, setWeaknessesContent] = useState('');
  const [opportunitiesContent, setOpportunitiesContent] = useState('');
  const [threatsContent, setThreatsContent] = useState('');
  const [moatsContent, setMoatsContent] = useState('');
  const [stockContent, setStockContent] = useState('');
  const [otherContent, setOtherContent] = useState('');

  const normalizeSymbol = (value?: string | null): string => {
    const raw = String(value ?? '').trim().toUpperCase();
    if (!raw) return '';
    const match = raw.match(/[A-Z0-9.-]+/);
    return match ? match[0] : raw;
  };

  const normalizedSymbol = normalizeSymbol(selectedStock);
  const canPersist = !!authToken && normalizedSymbol.length > 0;
  const canPersistNotes = !!authToken;
  const resolvedUserId = Number.isFinite(Number(authUserId)) ? Number(authUserId) : undefined;

  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [attachmentStatus, setAttachmentStatus] = useState<{ type: 'info' | 'success' | 'error'; text: string } | null>(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCategory, setNoteCategory] = useState<string>('General Notes');
  const [noteContent, setNoteContent] = useState('');
  const didInitKeyRef = useRef<string | null>(null);
  const suppressSaveRef = useRef(false);
  const saveTimerRef = useRef<number | undefined>(undefined);
  const dirtyRef = useRef(false);
  const lastSavedPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    selectedTabRef.current = selectedTab;
  }, [selectedTab]);

  const businessOperationsRef = useRef<any>(null);
  const strengthsRef = useRef<any>(null);
  const weaknessesRef = useRef<any>(null);
  const opportunitiesRef = useRef<any>(null);
  const threatsRef = useRef<any>(null);
  const moatsRef = useRef<any>(null);
  const stockRef = useRef<any>(null);
  const otherRef = useRef<any>(null);

  const [openPicker, setOpenPicker] = useState<{
    type: 'color' | 'background';
    tabId: string;
  } | null>(null);
  const [pickerAnchor, setPickerAnchor] = useState<DOMRect | null>(null);
  const [openEmojiPicker, setOpenEmojiPicker] = useState<{ tabId: string } | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<DOMRect | null>(null);
  const manualAlignOverrideRef = useRef<Record<string, boolean>>({});

  const quillRefs = useMemo(() => ({
    businessOperations: businessOperationsRef,
    strengths: strengthsRef,
    weaknesses: weaknessesRef,
    opportunities: opportunitiesRef,
    threats: threatsRef,
    moats: moatsRef,
    stock: stockRef,
    other: otherRef,
  }), []);

  const allQuillRefs = useMemo(() => (
    [businessOperationsRef, strengthsRef, weaknessesRef, opportunitiesRef, threatsRef, moatsRef, stockRef, otherRef]
  ), []);

  const quillRefByTabId: Record<string, React.RefObject<any>> = {
    'business-operations': businessOperationsRef,
    strengths: strengthsRef,
    weaknesses: weaknessesRef,
    opportunities: opportunitiesRef,
    threats: threatsRef,
    moats: moatsRef,
    stock: stockRef,
    other: otherRef,
  };

  const tabIdByName: Record<InsightTab, string> = {
    'Business Operations': 'business-operations',
    Strengths: 'strengths',
    Weaknesses: 'weaknesses',
    Opportunities: 'opportunities',
    Threats: 'threats',
    Moats: 'moats',
    Stock: 'stock',
    Other: 'other',
  };

  const normalizeHtmlText = (raw: string) =>
    String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const extractTitleFromContent = (html: string): string => {
    if (!html) return '';
    const blockRegex = /<(p|div|li|h1|h2|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi;
    let match = blockRegex.exec(html);
    while (match) {
      const text = normalizeHtmlText(match[2]);
      if (text) return text.split(/\s+/).slice(0, 5).join(' ');
      match = blockRegex.exec(html);
    }
    const fallback = normalizeHtmlText(html);
    if (!fallback) return '';
    return fallback.split(/\s+/).slice(0, 5).join(' ');
  };

  const hasNoteText = normalizeHtmlText(noteContent).length > 0;
  const canAddNote = noteCategory.trim().length > 0 && hasNoteText && noteTitle.trim().length > 0;

  useEffect(() => {
    const derivedTitle = extractTitleFromContent(noteContent);
    setNoteTitle(derivedTitle);
  }, [noteContent]);

  const openNewNote = () => {
    setNoteCategory('General Notes');
    setNoteTitle('');
    setNoteContent('');
    setIsNoteModalOpen(true);
  };

  const resizeQuillEditor = (ref: React.RefObject<any>) => {
    const editor = ref.current?.getEditor?.();
    const root = editor?.root as HTMLElement | undefined;
    const container = editor?.container as HTMLElement | undefined;
    if (!root) return;

    const scrollHost = root.closest('[data-insights-editor-scroll="1"]') as HTMLElement | null;
    const prevScrollTop = scrollHost ? scrollHost.scrollTop : null;

    root.style.height = 'auto';
    root.style.minHeight = '350px';
    root.style.overflow = 'hidden';
    const nextHeight = Math.max(root.scrollHeight, 350);
    root.style.height = `${nextHeight}px`;

    if (container) {
      container.style.height = 'auto';
    }

    if (scrollHost && prevScrollTop != null) {
      window.requestAnimationFrame(() => {
        scrollHost.scrollTop = prevScrollTop;
      });
    }
  };

  const insertImageFile = (editor: any, file: File) => {
    if (!file || !file.type || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (!result) return;
      const range = editor.getSelection?.(true) || { index: editor.getLength?.() || 0, length: 0 };
      editor.insertEmbed(range.index, 'image', result, 'user');
      editor.setSelection(range.index + 1, 0, 'silent');
    };
    reader.readAsDataURL(file);
  };

  const getAttachmentValidationError = (file: File): string | null => {
    if (!file) return 'Please select a file.';
    if (file.size <= 0) return 'File is empty.';
    if (file.size > INSIGHTS_ATTACHMENT_MAX_BYTES) {
      return 'File is too large (max 15MB).';
    }

    const extension = (() => {
      const idx = file.name.lastIndexOf('.');
      if (idx < 0) return '';
      return file.name.slice(idx).toLowerCase();
    })();
    if (!INSIGHTS_ATTACHMENT_ALLOWED_EXTENSIONS.has(extension)) {
      return 'Only PDF, DOC, and DOCX files are allowed.';
    }

    return null;
  };

  const insertAttachmentFile = async (editor: any, file: File) => {
    setAttachmentStatus(null);
    const validationError = getAttachmentValidationError(file);
    if (validationError) {
      setAttachmentStatus({ type: 'error', text: validationError });
      onNotify?.(validationError, 'error');
      return;
    }

    if (!authToken) {
      setAttachmentStatus({ type: 'error', text: 'Please login to upload attachments.' });
      onNotify?.('Please login to upload attachments.', 'error');
      return;
    }

    if (!normalizedSymbol) {
      setAttachmentStatus({ type: 'error', text: 'Please select a stock before uploading attachments.' });
      onNotify?.('Please select a stock before uploading attachments.', 'error');
      return;
    }

    setSyncBusy(true);
    setSyncError(null);
    setAttachmentStatus({ type: 'info', text: `Uploading ${file.name}...` });
    try {
      const uploaded = await adminApi.uploadMyResearchInsightsAttachment(authToken, normalizedSymbol, file);
      const range = editor.getSelection?.(true) || { index: editor.getLength?.() || 0, length: 0 };
      const linkText = (uploaded.fileName || file.name || 'Attachment').trim() || 'Attachment';
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const linkHref = `${origin}/#insights-attachment-${uploaded.id}`;
      editor.insertText(range.index, linkText, { link: linkHref }, 'user');
      editor.insertText(range.index + linkText.length, ' ', 'user');
      editor.setSelection(range.index + linkText.length + 1, 0, 'silent');
      dirtyRef.current = true;
      setAttachmentStatus({ type: 'success', text: `Inserted: ${linkText}` });
      onNotify?.('Attachment inserted.', 'success');
    } catch (e: any) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      const msg = e?.message || 'Failed to upload attachment';
      setSyncError(msg);
      setAttachmentStatus({ type: 'error', text: msg });
      onNotify?.(msg, 'error');
    } finally {
      setSyncBusy(false);
    }
  };

  // Load state from server per (userId, stockSymbol).
  useEffect(() => {
    if (!canPersist) return;

    const token = authToken as string;
    const key = `${token}:${normalizedSymbol}`;
    if (didInitKeyRef.current === key) return;

    let cancelled = false;
    (async () => {
      setSyncBusy(true);
      setSyncError(null);
      try {
        const res = await adminApi.getMyResearchInsightsState(token, normalizedSymbol);
        if (cancelled) return;

        const s = res;
        suppressSaveRef.current = true;
        dirtyRef.current = false;
        setBusinessOperationsContent((s?.businessOperationsHtml ?? '').toString());
        setStrengthsContent((s?.strengthsHtml ?? '').toString());
        setWeaknessesContent((s?.weaknessesHtml ?? '').toString());
        setOpportunitiesContent((s?.opportunitiesHtml ?? '').toString());
        setThreatsContent((s?.threatsHtml ?? '').toString());
        setMoatsContent((s?.moatsHtml ?? '').toString());
        setStockContent((s?.stockHtml ?? '').toString());
        setOtherContent((s?.otherHtml ?? '').toString());

        // Record snapshot so we don't immediately re-save identical content.
        lastSavedPayloadRef.current = JSON.stringify({
          stockSymbol: normalizedSymbol,
          businessOperationsHtml: (s?.businessOperationsHtml ?? '').toString(),
          strengthsHtml: (s?.strengthsHtml ?? '').toString(),
          weaknessesHtml: (s?.weaknessesHtml ?? '').toString(),
          opportunitiesHtml: (s?.opportunitiesHtml ?? '').toString(),
          threatsHtml: (s?.threatsHtml ?? '').toString(),
          moatsHtml: (s?.moatsHtml ?? '').toString(),
          stockHtml: (s?.stockHtml ?? '').toString(),
          otherHtml: (s?.otherHtml ?? '').toString(),
        });
        didInitKeyRef.current = key;
      } catch (e: any) {
        if (cancelled) return;
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        const msg = e?.message || 'Failed loading Insights';
        setSyncError(msg);
        onNotify?.('', 'info');
      } finally {
        if (!cancelled) setSyncBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authToken, canPersist, normalizedSymbol, onUnauthorized]);

  // Debounced save to server.
  useEffect(() => {
    if (!canPersist) return;
    const token = authToken as string;
    const key = `${token}:${normalizedSymbol}`;
    if (didInitKeyRef.current !== key) return;

    if (suppressSaveRef.current) {
      suppressSaveRef.current = false;
      return;
    }

    // Only save when user actually edited content.
    if (!dirtyRef.current) return;

    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }

    const payload = {
      stockSymbol: normalizedSymbol,
      businessOperationsHtml: businessOperationsContent ?? '',
      strengthsHtml: strengthsContent ?? '',
      weaknessesHtml: weaknessesContent ?? '',
      opportunitiesHtml: opportunitiesContent ?? '',
      threatsHtml: threatsContent ?? '',
      moatsHtml: moatsContent ?? '',
      stockHtml: stockContent ?? '',
      otherHtml: otherContent ?? '',
    };

    const payloadKey = JSON.stringify(payload);
    if (payloadKey === lastSavedPayloadRef.current) {
      dirtyRef.current = false;
      return;
    }

    saveTimerRef.current = window.setTimeout(async () => {
      setSyncBusy(true);
      setSyncError(null);
      try {
        await adminApi.upsertMyResearchInsightsState(token, payload);
        lastSavedPayloadRef.current = payloadKey;
        dirtyRef.current = false;
        onNotify?.(`${normalizedSymbol} • ${selectedTabRef.current} • Saved`, 'success');
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        const msg = e?.message || 'Failed saving Insights';
        setSyncError(msg);
        onNotify?.('', 'info');
      } finally {
        setSyncBusy(false);
      }
    }, 650);

    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [
    authToken,
    businessOperationsContent,
    strengthsContent,
    weaknessesContent,
    opportunitiesContent,
    threatsContent,
    moatsContent,
    stockContent,
    otherContent,
    canPersist,
    normalizedSymbol,
    onUnauthorized,
    onNotify,
  ]);

  // Memoize ReactQuill modules to prevent re-initialization on every render
  const quillModules = useMemo(() => {
    const tabId = tabIdByName[selectedTab];
    const getActiveQuill = () => quillRefByTabId[tabId]?.current?.getEditor?.();

    return {
      toolbar: {
        container: `#insights-toolbar-${selectedTab.toLowerCase().replace(/\s+/g, '-')}`,
        handlers: {
          undo: () => {
            getActiveQuill()?.history.undo();
          },
          redo: () => {
            getActiveQuill()?.history.redo();
          },
          image: () => {
            const editor = getActiveQuill();
            if (!editor) return;
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = () => {
              const file = input.files?.[0];
              if (file) insertImageFile(editor, file);
            };
            input.click();
          },
          attachment: () => {
            const editor = getActiveQuill();
            if (!editor) return;
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = INSIGHTS_ATTACHMENT_ACCEPT;
            input.onchange = async () => {
              const file = input.files?.[0];
              if (file) await insertAttachmentFile(editor, file);
            };
            input.click();
          },
        },
      },
      history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true
      }
    };
  }, [authToken, normalizedSymbol, onNotify, onUnauthorized, selectedTab]);

  useEffect(() => {
    const dropSubscriptions: Array<{ root: HTMLElement; handler: (event: DragEvent) => void }> = [];
    const pasteSubscriptions: Array<{ root: HTMLElement; handler: (event: ClipboardEvent) => void }> = [];
    const dragOverSubscriptions: Array<{ root: HTMLElement; handler: (event: DragEvent) => void }> = [];

    allQuillRefs.forEach((ref) => {
      if (!ref.current) return;
      const editor = ref.current.getEditor();
      const root = editor?.root as HTMLElement | undefined;
      if (!root) return;

      const dragOverHandler = (event: DragEvent) => {
        if (!event.dataTransfer) return;
        if (!Array.from(event.dataTransfer.types || []).includes('Files')) return;
        event.preventDefault();
      };

      const dropHandler = (event: DragEvent) => {
        if (!event.dataTransfer?.files?.length) return;
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);
        files.forEach((file) => insertImageFile(editor, file));
      };

      const pasteHandler = (event: ClipboardEvent) => {
        const items = event.clipboardData?.items ? Array.from(event.clipboardData.items) : [];
        if (items.length === 0) return;
        const imageItems = items.filter((item) => item.type && item.type.startsWith('image/'));
        if (imageItems.length === 0) return;
        event.preventDefault();
        imageItems.forEach((item) => {
          const file = item.getAsFile();
          if (file) insertImageFile(editor, file);
        });
      };

      root.addEventListener('dragover', dragOverHandler as EventListener);
      root.addEventListener('drop', dropHandler as EventListener);
      root.addEventListener('paste', pasteHandler as EventListener);
      dragOverSubscriptions.push({ root, handler: dragOverHandler });
      dropSubscriptions.push({ root, handler: dropHandler });
      pasteSubscriptions.push({ root, handler: pasteHandler });
    });

    return () => {
      dragOverSubscriptions.forEach(({ root, handler }) => {
        root.removeEventListener('dragover', handler as EventListener);
      });
      dropSubscriptions.forEach(({ root, handler }) => {
        root.removeEventListener('drop', handler as EventListener);
      });
      pasteSubscriptions.forEach(({ root, handler }) => {
        root.removeEventListener('paste', handler as EventListener);
      });
    };
  }, [allQuillRefs]);

  useEffect(() => {
    const clickHandler = async (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a') as HTMLAnchorElement | null;
      if (!anchor) return;

      // Handle only links inside quill editors in Insights.
      if (!anchor.closest('.ql-editor')) return;

      const href = (anchor.getAttribute('href') ?? '').trim();
      if (!href) return;

      const hashMatch = href.match(/#insights-attachment-(\d+)/i);
      const legacyMatch = href.match(/attachments\/(\d+)\/file/i);

      if (!hashMatch && !legacyMatch) {
        event.preventDefault();
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!authToken) {
        setAttachmentStatus({ type: 'error', text: 'Please login to open attachments.' });
        onNotify?.('Please login to open attachments.', 'error');
        return;
      }

      const id = hashMatch ? Number(hashMatch[1]) : (legacyMatch ? Number(legacyMatch[1]) : 0);
      if (!Number.isFinite(id) || id <= 0) {
        setAttachmentStatus({ type: 'error', text: 'Invalid attachment link.' });
        onNotify?.('Invalid attachment link.', 'error');
        return;
      }

      const popup = window.open('about:blank', '_blank');
      if (popup) {
        popup.opener = null;
        popup.document.title = 'Loading attachment...';
        popup.document.body.style.fontFamily = 'Arial, sans-serif';
        popup.document.body.style.padding = '20px';
        popup.document.body.textContent = 'Loading attachment...';
      } else {
        setAttachmentStatus({ type: 'info', text: 'Popup blocked, opening in current tab...' });
      }

      try {
        setAttachmentStatus({ type: 'info', text: 'Loading attachment...' });
        const blob = await adminApi.getMyResearchInsightsAttachmentFile(authToken, id);
        const blobUrl = URL.createObjectURL(blob);
        if (popup && !popup.closed) {
          popup.location.href = blobUrl;
        } else {
          window.location.assign(blobUrl);
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } catch (e: any) {
        if (popup && !popup.closed) {
          popup.close();
        }
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        const msg = e?.message || 'Failed opening attachment';
        setAttachmentStatus({ type: 'error', text: msg });
        onNotify?.(e?.message || 'Failed opening attachment', 'error');
      }
    };

    document.addEventListener('click', clickHandler, true);
    return () => {
      document.removeEventListener('click', clickHandler, true);
    };
  }, [authToken, onNotify, onUnauthorized]);

  const quillFormats = useMemo(() => [
    'bold', 'italic', 'underline', 'strike',
    'size',
    'list', 'bullet', 'indent',
    'link', 'image',
    'align', 'direction', 'color', 'background'
  ], []);

  // Auto-detect RTL/LTR per paragraph based on content
  useEffect(() => {
    const detectDirection = (text: string): 'rtl' | 'ltr' => {
      const match = text.match(/[A-Za-z\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/);
      if (!match) return 'ltr';
      const ch = match[0];
      return /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(ch) ? 'rtl' : 'ltr';
    };

    const applyDirectionPerLine = (editor: any) => {
      try {
        const lines = typeof editor.getLines === 'function' ? editor.getLines() : [];
        if (!lines || lines.length === 0) return;

        for (const line of lines) {
          const domNode = (line as any)?.domNode as HTMLElement | undefined;
          const text = (domNode?.textContent ?? '').trim();
          if (!text) continue;

          const dir = detectDirection(text);
          const nextDirection = dir === 'rtl' ? 'rtl' : false;
          const nextAlign = dir === 'rtl' ? 'right' : false;

          const formats = typeof (line as any)?.formats === 'function' ? (line as any).formats() : {};
          const autoFlag = Boolean(domNode?.dataset?.autoDir === '1');
          const hasManualFormat = Boolean(formats?.align || formats?.direction);

          if (!autoFlag && hasManualFormat) continue;

          if (autoFlag && (
            (formats?.align && formats.align !== nextAlign) ||
            (formats?.direction && formats.direction !== nextDirection)
          )) {
            if (domNode?.dataset) domNode.dataset.autoDir = '0';
            continue;
          }

          const index = typeof editor.getIndex === 'function' ? editor.getIndex(line) : 0;
          const length = typeof line?.length === 'function' ? line.length() : 1;
          editor.formatLine(index, Math.max(length, 1), { direction: nextDirection, align: nextAlign }, 'silent');
          if (domNode?.dataset) domNode.dataset.autoDir = '1';
        }
      } catch {
        // Ignore formatting failures when editor is not ready.
      }
    };

    const getTabIdForRef = (quillRef: any): string | null => {
      const entries = Object.entries(quillRefByTabId);
      for (const [tabId, ref] of entries) {
        if (ref === quillRef) return tabId;
      }
      return null;
    };

    const handleTextChange = (quillRef: any) => {
      if (quillRef && quillRef.current) {
        const editor = quillRef.current.getEditor();
        const tabId = getTabIdForRef(quillRef);
        
        const text = editor.getText().trim();
        if (text.length === 0) {
          if (tabId) {
            manualAlignOverrideRef.current[tabId] = false;
          }
          return;
        }

        if (tabId && manualAlignOverrideRef.current[tabId]) {
          return;
        }

        applyDirectionPerLine(editor);
      }
    };

    const subscriptions: Array<{ editor: any; handler: () => void }> = [];
    const selectionSubscriptions: Array<{ editor: any; handler: () => void }> = [];
    const inputSubscriptions: Array<{ root: HTMLElement; handler: (event: InputEvent) => void }> = [];
    const focusSubscriptions: Array<{ root: HTMLElement; handler: () => void }> = [];
    const keySubscriptions: Array<{ root: HTMLElement; handler: (event: KeyboardEvent) => void }> = [];

    // Set up text change listeners for all editors that are currently mounted.
    allQuillRefs.forEach((ref) => {
      if (ref.current) {
        const editor = ref.current.getEditor();
        const handler = () => handleTextChange(ref);
        editor.on('text-change', handler);
        subscriptions.push({ editor, handler });

        const selectionHandler = () => {
          const text = editor.getText().trim();
          if (text.length === 0) {
            const tabId = getTabIdForRef(ref);
            if (tabId) {
              manualAlignOverrideRef.current[tabId] = false;
            }
          }
        };
        editor.on('selection-change', selectionHandler);
        selectionSubscriptions.push({ editor, handler: selectionHandler });

        const root = editor.root as HTMLElement | undefined;
        if (root) {
          const inputHandler = () => {
            const text = editor.getText().trim();
            if (text.length !== 0) return;
            const tabId = getTabIdForRef(ref);
            if (tabId) {
              manualAlignOverrideRef.current[tabId] = false;
            }
          };

          root.addEventListener('beforeinput', inputHandler as EventListener);
          inputSubscriptions.push({ root, handler: inputHandler });

          const focusHandler = () => {
            const text = editor.getText().trim();
            if (text.length === 0) {
              const tabId = getTabIdForRef(ref);
              if (tabId) {
                manualAlignOverrideRef.current[tabId] = false;
              }
            }
          };
          root.addEventListener('focusin', focusHandler as EventListener);
          focusSubscriptions.push({ root, handler: focusHandler });

          const keyHandler = (event: KeyboardEvent) => {
            if (!event.ctrlKey || event.key !== 'Shift') return;
            if (event.location !== 1 && event.location !== 2) return;

            const tabId = getTabIdForRef(ref);
            if (tabId) {
              manualAlignOverrideRef.current[tabId] = true;
            }

            const selection = editor.getSelection?.();
            if (!selection) return;

            const dir = event.location === 2 ? 'rtl' : 'ltr';
            const nextDirection = dir === 'rtl' ? 'rtl' : false;
            const nextAlign = dir === 'rtl' ? 'right' : false;
            editor.formatLine(selection.index, Math.max(selection.length, 1), { direction: nextDirection, align: nextAlign }, 'user');
            event.preventDefault();
          };
          root.addEventListener('keydown', keyHandler as EventListener);
          keySubscriptions.push({ root, handler: keyHandler });
        }
      }
    });

    return () => {
      subscriptions.forEach(({ editor, handler }) => {
        editor.off('text-change', handler);
      });
      selectionSubscriptions.forEach(({ editor, handler }) => {
        editor.off('selection-change', handler);
      });
      inputSubscriptions.forEach(({ root, handler }) => {
        root.removeEventListener('beforeinput', handler as EventListener);
      });
      focusSubscriptions.forEach(({ root, handler }) => {
        root.removeEventListener('focusin', handler as EventListener);
      });
      keySubscriptions.forEach(({ root, handler }) => {
        root.removeEventListener('keydown', handler as EventListener);
      });
    };
  }, [selectedTab, allQuillRefs]);

  useEffect(() => {
    if (!openPicker && !openEmojiPicker) return;

    const onMouseDown = (ev: MouseEvent) => {
      const target = ev.target as Node | null;
      if (!target) return;
      const popover = document.getElementById('insights-color-popover');
      if (popover && popover.contains(target)) return;
      const emojiPopover = document.getElementById('insights-emoji-popover');
      if (emojiPopover && emojiPopover.contains(target)) return;
      setOpenPicker(null);
      setOpenEmojiPicker(null);
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setOpenPicker(null);
        setOpenEmojiPicker(null);
      }
    };

    const onScrollOrResize = () => {
      setOpenPicker(null);
      setOpenEmojiPicker(null);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [openEmojiPicker, openPicker]);

  const openColorPicker = (type: 'color' | 'background', tabId: string, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    setPickerAnchor(rect);
    setOpenEmojiPicker(null);
    setOpenPicker({ type, tabId });
  };

  const openEmojiMenu = (tabId: string, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    setEmojiAnchor(rect);
    setOpenPicker(null);
    setOpenEmojiPicker({ tabId });
  };

  const applyPickerColor = (color: string | null) => {
    if (!openPicker) return;
    const ref = quillRefByTabId[openPicker.tabId];
    const editor = ref?.current?.getEditor?.();
    if (!editor) return;
    editor.format(openPicker.type, color || false);
    setOpenPicker(null);
  };

  const applyEmoji = (emoji: string) => {
    if (!openEmojiPicker) return;
    const ref = quillRefByTabId[openEmojiPicker.tabId];
    const editor = ref?.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection?.(true) || { index: editor.getLength?.() || 0, length: 0 };
    editor.insertText(range.index, emoji, 'user');
    editor.setSelection(range.index + emoji.length, 0, 'silent');
    setOpenEmojiPicker(null);
  };

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      allQuillRefs.forEach((ref) => resizeQuillEditor(ref));
    });
    return () => window.cancelAnimationFrame(id);
  }, [
    selectedTab,
    businessOperationsContent,
    strengthsContent,
    weaknessesContent,
    opportunitiesContent,
    threatsContent,
    moatsContent,
    stockContent,
    otherContent,
    allQuillRefs,
  ]);

  const containerStyle: React.CSSProperties = {
    // Exact spacing between Header `tabs-bar` and `insights-tabs-bar`.
    marginTop: '15px',
    padding: '0 3rem 1rem 3rem',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    minHeight: 'calc(100vh - 15rem)',
    color: isDarkMode ? '#e5e7eb' : '#1f2937',
  };

  const tabsContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0rem',
    marginBottom: '0px',
    gap: '0px',
    borderBottom: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    // Keep the tabs visually closer to the underline.
    paddingBottom: '6px',
    width: '97%',
    marginLeft: '0rem',
  };

  const tabButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    width: '200px',
    height: '34px',
    backgroundColor: isSelected ? (isDarkMode ? '#374151' : '#0D0D0D') : 'transparent',
    color: isSelected ? '#ffffff' : (isDarkMode ? '#F9FAFB' : '#000000'),
    border: 'none',
    borderRadius: '6px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  const tabs: InsightTab[] = [
    'Business Operations',
    'Strengths',
    'Weaknesses',
    'Opportunities',
    'Threats',
    'Moats',
    'Stock',
    'Other'
  ];

  const editorStyle: React.CSSProperties = {
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    borderRadius: '6px',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    width: '97%',
    marginLeft: '0rem',
    minHeight: '400px',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  };

  const renderEditor = (tabId: string, content: string, setContent: (value: string) => void, quillRef: any) => {
    const toolbarId = `insights-toolbar-${tabId}`;
    const placeholderText = (() => {
      switch (tabId) {
        case 'business-operations':
          return 'Here you can write about Business Operations';
        case 'strengths':
          return 'Here you can write about Strengths';
        case 'weaknesses':
          return 'Here you can write about Weaknesses';
        case 'opportunities':
          return 'Here you can write about Opportunities';
        case 'threats':
          return 'Here you can write about Threats';
        case 'moats':
          return 'Here you can write about Moats';
        case 'stock':
          return 'Here you can write about Stock';
        case 'other':
          return 'Here you can write about Other';
        default:
          return undefined;
      }
    })();
    const editorFontSize = tabId === 'business-operations' ? '16px' : undefined;
    
    return (
      <div style={editorStyle} data-insights-editor-scroll="1">
        {/* Toolbar */}
        <div
          id={toolbarId}
          className="ql-toolbar ql-snow"
          style={{
          paddingTop: '12px',
          paddingBottom: '12px',
          paddingLeft: '15px',
          paddingRight: '15px',
          display: 'flex',
          gap: '17px',
          flexWrap: 'nowrap',
          background: isDarkMode ? '#111827' : 'white',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          overflow: 'visible',
          borderBottom: `1px solid ${isDarkMode ? '#374151' : '#ECECF4'}`,
          border: 'none',
          boxShadow: isDarkMode ? '0 6px 10px rgba(0,0,0,0.35)' : '0 6px 10px rgba(0,0,0,0.08)',
        }}>
          <span className="ql-formats" style={{ display: 'flex', gap: '14px', margin: 0 }}>
            <button className="ql-undo" title="Undo">
              <img src="/images/undo-icon.svg" alt="Undo" style={{ width: '18px', height: '18px' }} />
            </button>
            <button className="ql-redo" title="Redo">
              <img src="/images/redo-icon.svg" alt="Redo" style={{ width: '18px', height: '18px' }} />
            </button>
          </span>
          <span style={{ 
            display: 'inline-block',
            width: '1px', 
            height: '20px', 
            backgroundColor: '#D0D0D0',
            flexShrink: 0
          }}></span>
          <span className="ql-formats" style={{ display: 'flex', gap: '14px', margin: 0, alignItems: 'center' }}>
            <button className="ql-list" value="ordered" title="Ordered List"></button>
            <button className="ql-list" value="bullet" title="Bullet List"></button>
            <select className="ql-align" title="Text Alignment" style={{ margin: 0, marginLeft: '4px' }}></select>
          </span>
          <span style={{ 
            display: 'inline-block',
            width: '1px', 
            height: '20px', 
            backgroundColor: '#D0D0D0',
            flexShrink: 0
          }}></span>
          <span className="ql-formats" style={{ display: 'flex', gap: '14px', margin: 0, alignItems: 'center' }}>
            <button className="ql-indent" value="+1" title="Indent Increase"></button>
            <button className="ql-indent" value="-1" title="Indent Decrease"></button>
          </span>
          <span style={{ 
            display: 'inline-block',
            width: '1px', 
            height: '20px', 
            backgroundColor: '#D0D0D0',
            flexShrink: 0
          }}></span>
          <span className="ql-formats" style={{ display: 'flex', gap: '14px', margin: 0, alignItems: 'center' }}>
            <select className="ql-color" title="Text Color"></select>
            <select className="ql-background" title="Background Color"></select>
          </span>
          <span className="ql-formats" style={{ display: 'flex', gap: '14px', margin: 0 }}>
            <button
              className="ql-fontDecrease"
              title="Decrease Font"
              onClick={(e) => {
                e.preventDefault();
                const quill = quillRef.current?.getEditor?.();
                applyQuillFontSizeStep(quill, -1);
              }}
            >
              <img src="/images/a-arrow-down.svg" alt="A-" style={{ width: '24px', height: '24px' }} />
            </button>
            <button
              className="ql-fontIncrease"
              title="Increase Font"
              onClick={(e) => {
                e.preventDefault();
                const quill = quillRef.current?.getEditor?.();
                applyQuillFontSizeStep(quill, 1);
              }}
            >
              <img src="/images/a-arrow-up.svg" alt="A+" style={{ width: '24px', height: '24px' }} />
            </button>
          </span>
          <span style={{ 
            display: 'inline-block',
            width: '1px', 
            height: '20px', 
            backgroundColor: '#D0D0D0',
            flexShrink: 0
          }}></span>
          <span className="ql-formats" style={{ display: 'flex', gap: '14px', margin: 0 }}>
            <button className="ql-bold" title="Bold"></button>
            <button className="ql-italic" title="Italic"></button>
            <button className="ql-underline" title="Underline"></button>
            <button className="ql-strike" title="Strike"></button>
          </span>
          <span style={{ 
            display: 'inline-block',
            width: '1px', 
            height: '20px', 
            backgroundColor: '#D0D0D0',
            flexShrink: 0
          }}></span>
          <span className="ql-formats" style={{ display: 'flex', gap: '14px', margin: 0, alignItems: 'center' }}>
            <button
              className="ql-link"
              title="Insert Link"
              style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img src="/images/insert_link.svg" alt="Link" style={{ width: '24px', height: '24px' }} />
            </button>
            <button
              className="ql-mention"
              title="Mention (@)"
              style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={(e) => {
              e.preventDefault();
              const quill = quillRef.current?.getEditor();
              if (quill) {
                const range = quill.getSelection();
                if (range) {
                  quill.insertText(range.index, '@');
                  quill.setSelection(range.index + 1);
                }
              }
            }}>@</button>
            <button
              className="ql-emoji"
              title="Emoji"
              style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openEmojiMenu(tabId, e.currentTarget);
            }}>
              <img src="/images/smile.svg" alt="Emoji" style={{ width: '20px', height: '20px' }} />
            </button>
            <button
              className="ql-image"
              title="Insert Image"
              style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img src="/images/insertimage.svg?v=20260303" alt="Insert" style={{ width: '20px', height: '20px' }} />
            </button>
            <button
              className="ql-attachment"
              title="Insert File"
              style={{ width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <span aria-hidden="true" style={{ width: '28px', height: '28px', display: 'inline-block' }}></span>
            </button>
          </span>
        </div>

        {attachmentStatus && (
          <div
            style={{
              padding: '8px 15px 4px 15px',
              fontSize: '12px',
              color:
                attachmentStatus.type === 'error'
                  ? '#DC2626'
                  : attachmentStatus.type === 'success'
                    ? '#16A34A'
                    : (isDarkMode ? '#93C5FD' : '#1D4ED8'),
            }}
          >
            {attachmentStatus.text}
          </div>
        )}
        
        {/* Editor */}
        <div style={{ flex: 1, padding: '15px', border: 'none', position: 'relative', zIndex: 1 }}>
          <div style={{ 
            border: `1px solid ${isDarkMode ? '#374151' : '#ECECF4'}`,
            borderRadius: '4px',
            padding: '10px',
            minHeight: '350px',
            fontSize: editorFontSize,
          }}>
            <ReactQuill
              ref={quillRef}
              value={content}
              onChange={(value, _delta, source) => {
                setContent(value);
                if (source === 'user') {
                  dirtyRef.current = true;
                }
                resizeQuillEditor(quillRef);
              }}
              placeholder={placeholderText}
              style={{
                height: 'auto',
                minHeight: '330px',
                border: 'none',
              }}
              modules={quillModules}
              formats={quillFormats}
            />
          </div>
        </div>

        {openPicker && pickerAnchor && (
          <div
            id="insights-color-popover"
            style={{
              position: 'fixed',
              top: pickerAnchor.bottom + 6,
              left: pickerAnchor.left,
              zIndex: 10050,
              background: '#ffffff',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              padding: 8,
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 18px)', gap: 6 }}>
              {[
                '#000000', '#4B5563', '#9CA3AF', '#EF4444', '#F97316', '#F59E0B',
                '#10B981', '#22C55E', '#14B8A6', '#06B6D4', '#3B82F6', '#8B5CF6',
                '#EC4899', '#F472B6', '#FEE2E2', '#FEF3C7', '#DCFCE7', '#DBEAFE',
              ].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => applyPickerColor(c)}
                  style={{
                    width: 18,
                    height: 18,
                    background: c,
                    border: '1px solid #E5E7EB',
                    borderRadius: 3,
                    cursor: 'pointer',
                  }}
                  aria-label={`color-${c}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => applyPickerColor(null)}
              style={{
                marginTop: 8,
                width: '100%',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                padding: '4px 6px',
                fontSize: 12,
                cursor: 'pointer',
                background: '#F9FAFB',
              }}
            >
              Clear
            </button>
          </div>
        )}

        {openEmojiPicker && emojiAnchor && (
          <div
            id="insights-emoji-popover"
            style={{
              position: 'fixed',
              top: emojiAnchor.bottom + 6,
              left: emojiAnchor.left - 24,
              zIndex: 10050,
              background: '#ffffff',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              padding: 8,
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
              width: 252,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, gridAutoRows: '24px' }}>
              {[
                '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
                '😊', '😍', '😘', '😎', '🤔', '😴', '😮', '😇',
                '👍', '👎', '👏', '🙌', '🙏', '🤝', '💪', '🔥',
                '✨', '🎯', '📈', '📉', '💡', '📝', '✅', '❗',
              ].map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => applyEmoji(emoji)}
                  style={{
                    width: 24,
                    height: 24,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 18,
                    lineHeight: '24px',
                  }}
                  aria-label={`emoji-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      {/* Tabs Bar */}
      <div id="insights-tabs-bar" style={tabsContainerStyle}>
        {tabs.map((tab) => (
          <button
            key={tab}
            style={tabButtonStyle(selectedTab === tab)}
            onClick={() => setSelectedTab(tab)}
            onMouseEnter={(e) => {
              if (selectedTab !== tab) {
                e.currentTarget.style.opacity = '0.85';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTab !== tab) {
                e.currentTarget.style.opacity = '0.7';
              }
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content based on selected tab */}
      <div style={{ marginTop: '15px' }}>
        {selectedTab === 'Business Operations' && (
          <div id="business-operations-screen">
            {renderEditor('business-operations', businessOperationsContent, setBusinessOperationsContent, quillRefs.businessOperations)}
          </div>
        )}
        
        {selectedTab === 'Strengths' && (
          <div id="strengths-screen">
            {renderEditor('strengths', strengthsContent, setStrengthsContent, quillRefs.strengths)}
          </div>
        )}
        
        {selectedTab === 'Weaknesses' && (
          <div id="weaknesses-screen">
            {renderEditor('weaknesses', weaknessesContent, setWeaknessesContent, quillRefs.weaknesses)}
          </div>
        )}
        
        {selectedTab === 'Opportunities' && (
          <div id="opportunities-screen">
            {renderEditor('opportunities', opportunitiesContent, setOpportunitiesContent, quillRefs.opportunities)}
          </div>
        )}
        
        {selectedTab === 'Threats' && (
          <div id="threats-screen">
            {renderEditor('threats', threatsContent, setThreatsContent, quillRefs.threats)}
          </div>
        )}
        
        {selectedTab === 'Moats' && (
          <div id="moats-screen">
            {renderEditor('moats', moatsContent, setMoatsContent, quillRefs.moats)}
          </div>
        )}
        
        {selectedTab === 'Stock' && (
          <div id="stock-screen">
            {renderEditor('stock', stockContent, setStockContent, quillRefs.stock)}
          </div>
        )}

        {selectedTab === 'Other' && (
          <div id="other-screen">
            {renderEditor('other', otherContent, setOtherContent, quillRefs.other)}
          </div>
        )}
      </div>

      <FloatingNewNoteButton
        visible={true}
        onClick={openNewNote}
        label="New Note"
        backgroundColor={isDarkMode ? '#374151' : undefined}
      />

      <ResearchNoteModal
        isOpen={isNoteModalOpen}
        onRequestClose={() => setIsNoteModalOpen(false)}
        isDarkMode={isDarkMode}
        noteTitle={noteTitle}
        onChangeTitle={setNoteTitle}
        noteCategory={noteCategory}
        onChangeCategory={setNoteCategory}
        noteContent={noteContent}
        onChangeContent={setNoteContent}
        categories={RESEARCH_DESK_NOTE_CATEGORIES}
        canSubmit={canAddNote}
        onSubmit={async () => {
          if (!canPersistNotes) {
            onNotify?.('Please login to save notes.', 'error');
            return;
          }

          const title = extractTitleFromContent(noteContent);
          if (!title) return;

          try {
            await adminApi.createResearchDeskNote(authToken as string, {
              userId: resolvedUserId,
              title,
              associatedWith: noteCategory.trim(),
              stockSymbol: normalizedSymbol || String(selectedStock ?? '').trim().toUpperCase(),
              html: noteContent ?? '',
            });
            setIsNoteModalOpen(false);
            setNoteTitle('');
            setNoteContent('');
            setNoteCategory('General Notes');
            const symbolText = normalizedSymbol || String(selectedStock ?? '').trim().toUpperCase() || 'N/A';
            onNotify?.(`${symbolText} • ${noteCategory.trim()} • Saved`, 'success');
          } catch (e: any) {
            if (isUnauthorized(e)) {
              onUnauthorized?.();
              return;
            }
            onNotify?.(e?.message || 'Failed to save note', 'error');
          }
        }}
      />

      {/* Quill Toolbar Custom Styling */}
      <style>{`
        [id^="insights-toolbar-"] .ql-formats {
          margin-right: 0 !important;
        }
        
        [id^="insights-toolbar-"] button,
        [id^="insights-toolbar-"] .ql-picker {
          background: ${isDarkMode ? '#111827' : 'white'};
          border: none;
          cursor: pointer;
          padding: 4px 6px;
          border-radius: 4px;
          transition: background-color 0.2s;
          min-width: 28px;
          min-height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        
        [id^="insights-toolbar-"] .ql-picker-label {
          border: none;
          padding: 6px 8px;
          display: flex;
          align-items: center;
          min-width: 32px;
          min-height: 32px;
          background: ${isDarkMode ? '#111827' : 'transparent'};
        }
        
        [id^="insights-toolbar-"] .ql-align {
          display: flex;
          align-items: center;
        }

        [id^="insights-toolbar-"] .ql-align .ql-picker-label {
          padding: 0 !important;
          width: 32px !important;
          height: 32px !important;
        }

        [id^="insights-toolbar-"] .ql-align .ql-picker-label svg {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-align .ql-picker-label::before {
          content: '' !important;
          display: block !important;
          width: 32px !important;
          height: 32px !important;
          background-image: url('/images/align.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        
        [id^="insights-toolbar-"] .ql-color .ql-picker-label,
        [id^="insights-toolbar-"] .ql-background .ql-picker-label {
          padding: 0 !important;
          width: 28px !important;
          height: 28px !important;
        }
        
        [id^="insights-toolbar-"] .ql-color .ql-picker-label svg,
        [id^="insights-toolbar-"] .ql-background .ql-picker-label svg {
          display: none !important;
        }
        
        [id^="insights-toolbar-"] .ql-color .ql-picker-label::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/font-color.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          margin: 4px !important;
        }
        
        [id^="insights-toolbar-"] .ql-background .ql-picker-label::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/edit-editor.svg?v=20260303') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          margin: 4px !important;
        }

        [id^="insights-toolbar-"] .ql-image svg,
        [id^="insights-toolbar-"] .ql-image img {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-image {
          width: 24px !important;
          height: 24px !important;
          min-width: 24px !important;
          min-height: 24px !important;
          padding: 0 !important;
          overflow: visible !important;
        }

        [id^="insights-toolbar-"] .ql-image::before {
          content: '' !important;
          display: block !important;
          width: 24px !important;
          height: 24px !important;
          background-image: url('/images/insertimage.svg?v=20260303') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        
        [id^="insights-toolbar-"] .ql-tooltip {
          position: absolute !important;
          z-index: 10004 !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
        }
        
        [id^="insights-toolbar-"] .ql-tooltip.ql-editing {
          left: 50% !important;
          transform: translateX(-50%) !important;
        }
        
        [id^="insights-toolbar-"] button img {
          vertical-align: middle;
          width: 20px;
          height: 20px;
          display: block;
        }

        [id^="insights-toolbar-"] .ql-link svg,
        [id^="insights-toolbar-"] .ql-link img {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-link::before {
          content: '' !important;
          display: block !important;
          width: 24px !important;
          height: 24px !important;
          background-image: url('/images/insert_link.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-attachment svg,
        [id^="insights-toolbar-"] .ql-attachment img {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-attachment::before {
          content: '' !important;
          display: block !important;
          width: 30px !important;
          height: 30px !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21.44 11.05L12.25 20.24a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.2a2 2 0 1 1-2.83-2.84l8.49-8.48'/%3E%3C/svg%3E") !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-list svg {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-list::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/list_check_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-indent svg {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-indent[value="-1"]::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/list-indent-increase.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-indent[value="+1"]::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/list-indent-decrease.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-bold svg {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-bold::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/bold_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-italic svg {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-italic::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/italic_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-underline svg {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-underline::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/underline_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }

        [id^="insights-toolbar-"] .ql-strike svg {
          display: none !important;
        }

        [id^="insights-toolbar-"] .ql-strike::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/strikethrough_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        
        [id^="insights-toolbar-"] button:hover,
        [id^="insights-toolbar-"] .ql-picker:hover {
          background-color: #f3f4f6;
        }
        
        [id^="insights-toolbar-"] button.ql-active {
          background-color: #e5e7eb;
        }
        
        [id^="insights-toolbar-"] .ql-list svg,
        [id^="insights-toolbar-"] .ql-bold svg,
        [id^="insights-toolbar-"] .ql-italic svg,
        [id^="insights-toolbar-"] .ql-underline svg,
        [id^="insights-toolbar-"] .ql-strike svg {
          width: 20px !important;
          height: 20px !important;
        }
        
        [id^="insights-toolbar-"] .ql-align .ql-picker-label svg {
          width: 20px !important;
          height: 20px !important;
        }
        
        [id^="insights-toolbar-"] .ql-mention {
          font-size: 0 !important;
          line-height: 1;
          padding: 0 !important;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 28px;
          width: 28px;
        }
        [id^="insights-toolbar-"] .ql-mention::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/mention.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        
        .ql-editor {
          font-size: 16px;
          line-height: 1.6;
          min-height: 350px;
          border: none !important;
        }
        
        .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }

        .ql-editor.ql-blank {
          background-color: ${isDarkMode ? '#111827' : 'transparent'};
        }

        .ql-editor a {
          cursor: pointer;
          pointer-events: auto;
          text-decoration: underline;
        }
        
        .ql-container {
          border: none !important;
        }

        .ql-container.ql-snow {
          background-color: ${isDarkMode ? '#111827' : 'transparent'};
        }
        
        .ql-tooltip {
          left: 50px !important;
          z-index: 10000 !important;
        }
      `}</style>
    </div>
  );
};

export default ResearchInsightsScreen;
